import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/providers/I18nProvider';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmCtx = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => setState({ ...opts, resolve }));
  }, []);

  const close = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={{ confirm }}>
      {children}
      {state ? (
        <Modal open onClose={() => close(false)} title={state.title ?? t('general.confirm')}>
          <p className="confirm__message">{state.message}</p>
          <div className="confirm__actions">
            <Button variant="ghost" onClick={() => close(false)}>
              {state.cancelLabel ?? t('general.cancel')}
            </Button>
            <Button
              variant={state.destructive ? 'danger' : 'primary'}
              onClick={() => close(true)}
              autoFocus
            >
              {state.confirmLabel ?? (state.destructive ? t('general.delete') : t('general.confirm'))}
            </Button>
          </div>
        </Modal>
      ) : null}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
